import { Search, Upload } from 'lucide-react';
import React from 'react';
import LogoAnimate from '../../assets/images/LogoAnimate.svg';

/**
 * Professional Empty Results State Component
 * Shows when no search has been performed yet
 *
 * @param {Object} props
 * @param {Function} props.onUploadClick - Optional upload callback
 */
export function EmptyResultsState({ onUploadClick }) {
  return (
    <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 p-12">
      {/* Animated Logo */}
      <div className="mb-8 animate-pulse">
        <img
          src={LogoAnimate}
          alt="ALICE"
          className="h-32 w-32 opacity-80"
        />
      </div>

      {/* Main Message */}
      <div className="text-center max-w-md">
        <h3 className="text-2xl font-bold text-gray-800 mb-3">
          Ready to Find Your Perfect Candidates?
        </h3>
        <p className="text-gray-600 mb-8 leading-relaxed">
          Start by uploading a job description or selecting from your previous searches.
          ALICE will help you find and evaluate the best candidates.
        </p>
      </div>

      {/* Quick Start Guide */}
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-sm font-bold">?</span>
          How to Get Started
        </h4>

        <div className="space-y-4">
          {/* Step 1 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <span className="text-orange-600 font-bold">1</span>
              </div>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Upload or Select a Job Description</h5>
              <p className="text-sm text-gray-600">
                Click "Select Job" above to choose from previous searches, or upload a new job description to ALICE.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 font-bold">2</span>
              </div>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Enter Your Search Query</h5>
              <p className="text-sm text-gray-600">
                Describe what you're looking for in natural language, like "Find candidates with 5+ years Python experience in New York."
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 font-bold">3</span>
              </div>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Review & Evaluate Results</h5>
              <p className="text-sm text-gray-600">
                ALICE will find matching candidates, and you can evaluate, contact, and manage them all from this interface.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Preview */}
      <div className="mt-8 flex items-center gap-6 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4" />
          <span>AI-Powered Search</span>
        </div>
        <div className="w-px h-4 bg-gray-300"></div>
        <div className="flex items-center gap-2">
          <Upload className="w-4 h-4" />
          <span>Multiple Formats Supported</span>
        </div>
        <div className="w-px h-4 bg-gray-300"></div>
        <div className="flex items-center gap-2">
          <span className="w-4 h-4 text-green-500">✓</span>
          <span>Instant Results</span>
        </div>
      </div>
    </div>
  );
}

export default EmptyResultsState;























